import { supabase } from '@/lib/supabase';
import { Database } from '@/lib/database.types';
import {
  ImportSession,
  ImportProgress,
  ImportResult,
  ParsedChannelData,
  ImportFormData,
  ImportOptions
} from '@/features/channels/types/import';
import { sequentialImportService } from '@/services/import/sequentialImportService';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { toast } from 'sonner';

type Json = Database['public']['Tables']['import_sessions']['Row']['error_log'];

export interface ChannelNumberInsert {
  channel_id: number;
  type_id: number;
  number: string;
  is_sequence: boolean;
}

class ImportService {
  async getLastSequenceNumber(providerId: number, versionId: number): Promise<string> {
    logger.info(LOG_CONTEXTS.IMPORT, 'Getting last sequence number', {
      providerId,
      versionId
    });
    
    try {
      // Force a direct database query with stale=0 to bypass any caching
      const { data, error } = await supabase
        .rpc('get_next_sequence_number', {
          p_provider_id: providerId,
          p_version_id: versionId
        });

      if (error) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Failed to get last sequence number', {
          error,
          providerId,
          versionId
        });
        throw error;
      }

      const lastNumber = String(data - 1);
      logger.info(LOG_CONTEXTS.IMPORT, 'Last sequence number retrieved', {
        nextNumber: data,
        lastNumber,
        providerId,
        versionId
      });
      
      return lastNumber;
    } catch (error) {
      logger.error(LOG_CONTEXTS.IMPORT, 'Error in getLastSequenceNumber', { error });
      throw error;
    }
  }

  async refreshSequenceNumbers(providerId: number, versionId: number): Promise<void> {
    logger.info(LOG_CONTEXTS.IMPORT, 'Forcing sequence number refresh', {
      providerId,
      versionId
    });
    
    try {
      // Call the resequence function to ensure all numbers are properly ordered
      const { error } = await supabase.rpc('resequence_channel_numbers', {
        p_provider_id: providerId,
        p_version_id: versionId
      });
      
      if (error) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Failed to resequence channel numbers', { error });
        throw error;
      }
      
      logger.info(LOG_CONTEXTS.IMPORT, 'Successfully refreshed sequence numbers');
    } catch (error) {
      logger.error(LOG_CONTEXTS.IMPORT, 'Error in refreshSequenceNumbers', { error });
      throw error;
    }
  }

  private async generateSequentialNumbers(
    data: ParsedChannelData[],
    providerId: number,
    versionId: number,
    typeId: number
  ): Promise<ParsedChannelData[]> {
    const { data: typeData, error: typeError } = await supabase
      .from('channel_types')
      .select('*')
      .eq('id', typeId)
      .single();

    if (typeError || !typeData) {
      throw new Error('Failed to get channel type details');
    }

    const lastNumber = await this.getLastSequenceNumber(providerId, versionId);
    let currentNumber = Number(lastNumber) + 1;

    return data.map((item: ParsedChannelData): ParsedChannelData => ({
      ...item,
      numbers: [{
        typeId,
        number: String(currentNumber++),
        isSequence: true,
        is_default: typeData.is_default || false
      }],
      name: item.name,
      line: item.line,
      categoryId: item.categoryId,
      planIds: item.planIds || []
    }));
  }

  async createSession(
    providerId: number,
    versionId: number,
    formData: ImportFormData
  ): Promise<ImportSession> {
    const { data, error } = await supabase
      .from('import_sessions')
      .insert({
        provider_id: providerId,
        version_id: versionId,
        format_type: formData.format,
        category_id: formData.categoryId,
        plan_ids: formData.planIds,
        status: 'pending',
        total_items: 0,
        processed_items: 0,
        error_log: []
      })
      .select()
      .single();

    if (error) throw error;
    return data as unknown as ImportSession;
  }

  async processImport(
    sessionId: number,
    data: ParsedChannelData[],
    options: ImportOptions
  ): Promise<ImportResult> {
    const { providerId, versionId } = options;
    const progress: ImportProgress = {
      total: data.length,
      processed: 0,
      success: 0,
      failed: 0,
      errors: []
    };

    try {
      // First get provider details
      const { data: provider, error: providerError } = await supabase
        .from('providers')
        .select('has_sequence_numbers')
        .eq('id', providerId)
        .single();

      if (providerError || !provider) {
        throw new Error('Provider not found');
      }

      // If sequential, get the sequence type
      let processedData = data;
      if (provider.has_sequence_numbers) {
        const { data: sequenceType, error: typeError } = await supabase
          .from('channel_types')
          .select('*')
          .eq('provider_id', providerId)
          .eq('version_id', versionId)
          .eq('is_sequence', true)
          .single();

        if (typeError || !sequenceType) {
          throw new Error('No sequence type found for this provider version');
        }

        processedData = await this.generateSequentialNumbers(
          data,
          providerId,
          versionId,
          sequenceType.id
        );
      }

      // Choose import strategy based on provider type
      if (provider.has_sequence_numbers) {
        return this.processSequentialImport(sessionId, processedData, options, progress);
      } else {
        return this.processNonSequentialImport(sessionId, processedData, options, progress);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An unknown error occurred during import");
      throw error;
    }
  }

  private async processNonSequentialImport(
    sessionId: number,
    data: ParsedChannelData[],
    options: ImportOptions,
    progress: ImportProgress
  ): Promise<ImportResult> {
    await this.updateSessionStatus(sessionId, 'processing', data.length);
    
    const results: ImportResult = {
      sessionId,
      success: [],
      failures: [],
      progress: {
        total: data.length,
        processed: 0,
        success: 0,
        failed: 0,
        errors: []
      }
    };

    // Process channels one by one for better error handling
    for (const channel of data) {
      try {
        // Normalize channel numbers before processing
        const normalizedChannel = {
          ...channel,
          numbers: channel.numbers.map(num => ({
            ...num,
            // Ensure asterisk is properly handled
            number: !num.number || num.number.trim() === '' ? '*' : num.number
          }))
        };

        const { data: importResult, error: importError } = await supabase.rpc(
          'process_non_sequential_channel_import',
          {
            p_data: [normalizedChannel],
            p_provider_id: options.providerId,
            p_version_id: options.versionId,
            p_category_ids: options.categoryId ? [options.categoryId] : [],
            p_default_category_id: options.categoryId ?? null,
            p_plan_ids: options.planIds ?? [],
            p_is_local: options.is_local ?? false,
            p_is_duplicate: options.is_duplicate ?? false
          }
        );

        if (importError) {
          results.failures.push({
            data: channel,
            error: {
              line: channel.line,
              message: importError.message,
              type: 'process',
              field: 'import'
            }
          });
          progress.failed++;
        } else if (Array.isArray(importResult) && importResult.length > 0) {
          const result = importResult[0];
          if (result.success) {
            results.success.push(channel);
            progress.success++;

            // Track channel entity creation for non-sequential import
            try {
              if (result.channel_id) {
                // Get version data for region_id
                const { data: version } = await supabase
                  .from('provider_versions')
                  .select('region_id')
                  .eq('id', options.versionId)
                  .single();

                if (version) {
                  const { changeTrackingService } = await import('../changeTracking');

                  await changeTrackingService.logEntityCreation({
                    versionId: options.versionId,
                    providerId: options.providerId,
                    regionId: version.region_id,
                    entityType: 'channels',
                    entityId: result.channel_id,
                    entityData: {
                      id: result.channel_id,
                      name: channel.name,
                      description: channel.description,
                      price: channel.price,
                      category_id: options.categoryId,
                      is_local: options.is_local || false,
                      is_duplicate: options.is_duplicate || false
                    }
                  });
                }
              }
            } catch (trackingError) {
              console.error('Failed to track channel creation in non-sequential import:', trackingError);
            }
          } else {
            results.failures.push({
              data: channel,
              error: {
                line: channel.line,
                message: result.error_message || 'Unknown error',
                type: 'process',
                field: 'import'
              }
            });
            progress.failed++;
          }
        } else {
          results.failures.push({
            data: channel,
            error: {
              line: channel.line,
              message: 'No result returned from import function',
              type: 'process',
              field: 'import'
            }
          });
          progress.failed++;
        }

        progress.processed++;
        await this.updateSessionProgress(sessionId, progress.processed, progress.errors);
        
      } catch (error) {
        results.failures.push({
          data: channel,
          error: {
            line: channel.line,
            message: error instanceof Error ? error.message : 'Unknown error',
            type: 'process',
            field: 'import'
          }
        });
        progress.failed++;
      }
    }

    // Update final status
    const finalStatus = progress.failed > 0 ? 'completed_with_errors' : 'completed';
    await this.updateSessionStatus(sessionId, finalStatus, data.length, results.failures.map(f => f.error) as unknown as Json);

    return results;
  }

  private async processSequentialImport(
    sessionId: number,
    data: ParsedChannelData[],
    options: ImportOptions,
    progress: ImportProgress
  ): Promise<ImportResult> {
    const results: ImportResult = {
      sessionId,
      success: [],
      failures: [],
      progress
    };

    try {
      logger.info(LOG_CONTEXTS.IMPORT, 'Processing sequential import', { 
        sessionId, 
        dataCount: data.length,
        providerId: options.providerId,
        versionId: options.versionId
      });
      
      // Update session status to processing
      await this.updateSessionStatus(sessionId, 'processing', data.length);

      // Normalize data before processing
      const normalizedData = data.map(item => ({
        name: item.name,
        description: item.description || null,
        price: item.price || null,
        category_id: options.categoryId
      }));

      // Use the enhanced sequentialImportService for bulk processing
      const importedData = await sequentialImportService.processBulkImport(
        normalizedData,
        options.providerId,
        options.versionId,
        options.categoryId,
        options.planIds,
        options.is_local || false,
        options.is_duplicate || false
      );

      logger.info(LOG_CONTEXTS.IMPORT, 'Sequential import processed by service', {
        sessionId,
        resultCount: Array.isArray(importedData) ? importedData.length : 0
      });

      // Update progress and session
      results.success = data;
      progress.success += data.length;
      progress.processed += data.length;

      // Update all imported items as processed
      await supabase.from('import_sessions')
        .update({
          processed_items: data.length,
          status: 'completed'
        })
        .eq('id', sessionId);

      logger.info(LOG_CONTEXTS.IMPORT, 'Sequential import completed', {
        sessionId,
        success: progress.success,
        total: progress.total
      });

    } catch (error) {
      logger.error(LOG_CONTEXTS.IMPORT, 'Sequential import error', { 
        error,
        sessionId
      });
      
      results.failures = data.map(channel => ({
        data: channel,
        error: {
          line: channel.line,
          message: error instanceof Error ? error.message : 'Unknown error',
          type: 'process',
          field: 'import'
        }
      }));
      progress.failed += data.length;

      // Update session status
      await this.updateSessionStatus(
        sessionId, 
        'completed_with_errors',
        data.length,
        results.failures.map(f => f.error) as unknown as Json
      );
    }

    return results;
  }

  // Main import method
  async startImport(
    sessionId: number, 
    data: ParsedChannelData[], 
    options: ImportOptions
  ): Promise<ImportResult> {
    const progress: ImportProgress = {
      total: data.length,
      processed: 0,
      success: 0,
      failed: 0,
      errors: []
    };

    // Check if provider is sequential
    const { data: provider } = await supabase
      .from('providers')
      .select('has_sequence_numbers')
      .eq('id', options.providerId)
      .single();

    if (provider?.has_sequence_numbers) {
      return this.processSequentialImport(sessionId, data, options, progress);
    } else {
      return this.processNonSequentialImport(sessionId, data, options, progress);
    }
  }

  public async getSession(sessionId: number): Promise<ImportSession> {
    const { data, error } = await supabase
      .from('import_sessions')
      .select()
      .eq('id', sessionId)
      .single();

    if (error) throw error;
    return data as unknown as ImportSession;
  }

  private async updateSessionStatus(
    sessionId: number,
    status: string,
    totalItems?: number,
    errorLog?: unknown
  ) {
    const update: any = {
      status,
      ...(totalItems !== undefined ? { total_items: totalItems } : {}),
      ...(errorLog !== undefined ? { error_log: errorLog as Json } : {})
    };

    const { error } = await supabase
      .from('import_sessions')
      .update(update)
      .eq('id', sessionId);

    if (error) throw error;
  }

  private async updateSessionProgress(
    sessionId: number,
    processed: number,
    errors: unknown
  ) {
    const { error } = await supabase
      .from('import_sessions')
      .update({
        processed_items: processed,
        error_log: errors as Json
      })
      .eq('id', sessionId);

    if (error) throw error;
  }

  async subscribeToImportProgress(
    sessionId: number,
    onProgress: (progress: ImportProgress) => void
  ): Promise<() => void> {
    const channel = supabase.channel(`import-${sessionId}`);
    
    channel
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'import_sessions',
          filter: `id=eq.${sessionId}`
        },
        (payload: { new: ImportSession }) => {
          const session = payload.new;
          onProgress({
            total: session.total_items,
            processed: session.processed_items,
            success: session.processed_items - session.error_log.length,
            failed: session.error_log.length,
            errors: session.error_log
          });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }

  async getSessionStatus(sessionId: number): Promise<ImportSession['status']> {
    const { data, error } = await supabase
      .from('import_sessions')
      .select('status')
      .eq('id', sessionId)
      .single();

    if (error) throw error;
    return data.status as ImportSession['status'];
  }

  async resumeFailedImport(sessionId: number): Promise<void> {
    const { error } = await supabase
      .from('import_sessions')
      .update({ status: 'pending' })
      .eq('id', sessionId);

    if (error) throw error;
  }
}

export const importService = new ImportService();
